import React, { useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import formOptions from "../../config/formOptions.json";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton.jsx";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config";
import { useTenant } from "../../context/TenantContext";

import { CREATE_OPERATIONAL_AUDIT } from "../../graph/operationalAudits/mutations";
import { READ_AUDIT_AREA_SUMMARIES } from "../../graph/operationalAudits/queries";

const client = new GraphQLClient(graphqlEndpoint);

const auditSchema = yup.object().shape({
  name: yup.string().trim().required("Audit name is required"),

  industry: yup
    .string()
    .trim()
    .required("Industry is required"),

  areas: yup
    .array()
    .of(yup.string().required())
    .min(1, "Select at least one operational area")
    .required("Select at least one operational area"),
});

const initialAuditValues = {
  name: "",
  industry: "",
  areas: [],
};

const formatEnumLabel = (value) => {
  if (!value) {
    return "";
  }

  return value
    .toLowerCase()
    .split("_")
    .map(
      (word) =>
        word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
};

const CreateOperationalAudits = () => {
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();
  const { tenantId } = useTenant();

  const [areaSummaries, setAreaSummaries] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  const loadAreaSummaries = async (industry) => {
    if (!industry) {
      setAreaSummaries([]);
      return;
    }

    setLoadingAreas(true);

    try {
      const data = await client.request(
        READ_AUDIT_AREA_SUMMARIES,
        {
          industry,
        },
      );

      const summaries = Array.isArray(
        data?.readAuditAreaSummaries,
      )
        ? data.readAuditAreaSummaries
        : [];

      setAreaSummaries(
        summaries.map((summary) => ({
          area: summary.area,
          questionCount:
            Number(summary.questionCount) || 0,
        })),
      );
    } catch (error) {
      console.error(
        "Failed to load audit area summaries:",
        error,
      );

      setAreaSummaries([]);

      setSnackbar({
        open: true,
        severity: "error",
        message:
          error?.response?.errors?.[0]?.message ||
          "Failed to load available operational areas",
      });
    } finally {
      setLoadingAreas(false);
    }
  };

  const handleFormSubmit = async (
    values,
    { resetForm, setSubmitting },
  ) => {
    try {
      if (!tenantId) {
        throw new Error("No tenancy is currently selected");
      }

      const selectedAreas = Array.isArray(values.areas)
        ? values.areas
        : [];

      if (selectedAreas.length === 0) {
        throw new Error(
          "Select at least one operational area",
        );
      }

      const input = {
        tenantId,
        companyId: null,
        name: values.name.trim(),
        industry: values.industry,
        areas: selectedAreas,
      };

      await client.request(CREATE_OPERATIONAL_AUDIT, {
        input,
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Operational audit created successfully",
      });

      resetForm();
      setAreaSummaries([]);

      navigate("/manageOperationalAudits");
    } catch (error) {
      console.error(
        "Error creating operational audit:",
        error,
      );

      setSnackbar({
        open: true,
        severity: "error",
        message:
          error?.response?.errors?.[0]?.message ||
          error?.message ||
          "Failed to create operational audit",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Header
        title="CREATE OPERATIONAL AUDIT"
        subtitle="Create a focused operational health check for the selected tenancy"
      />

      <Formik
        initialValues={initialAuditValues}
        validationSchema={auditSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
        }) => {
          const selectedAreas = Array.isArray(
            values.areas,
          )
            ? values.areas
            : [];

          const availableAreaSummaries =
            areaSummaries.filter(
              (summary) => summary.questionCount > 0,
            );

          const totalAvailableQuestions =
            areaSummaries.reduce(
              (total, summary) =>
                total + summary.questionCount,
              0,
            );

          const selectedQuestionCount =
            areaSummaries.reduce((total, summary) => {
              if (
                selectedAreas.includes(summary.area)
              ) {
                return total + summary.questionCount;
              }

              return total;
            }, 0);

          const allAvailableAreasSelected =
            availableAreaSummaries.length > 0 &&
            availableAreaSummaries.every((summary) =>
              selectedAreas.includes(summary.area),
            );

          const handleIndustryChange = async (event) => {
            const nextIndustry = event.target.value;

            await setFieldValue(
              "industry",
              nextIndustry,
            );

            await setFieldValue("areas", []);
            await setFieldTouched("areas", false);

            setAreaSummaries([]);

            await loadAreaSummaries(nextIndustry);
          };

          const handleAreaChange = async (
            area,
            checked,
          ) => {
            const nextAreas = checked
              ? Array.from(
                  new Set([...selectedAreas, area]),
                )
              : selectedAreas.filter(
                  (selectedArea) =>
                    selectedArea !== area,
                );

            await setFieldValue("areas", nextAreas);
            await setFieldTouched("areas", true);
          };

          const handleSelectAllAreas = async () => {
            const availableAreas =
              availableAreaSummaries.map(
                (summary) => summary.area,
              );

            await setFieldValue(
              "areas",
              availableAreas,
            );

            await setFieldTouched("areas", true);
          };

          const handleClearAllAreas = async () => {
            await setFieldValue("areas", []);
            await setFieldTouched("areas", true);
          };

          return (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              >
                <TextField
                  fullWidth
                  label="Audit Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(
                    touched.name && errors.name,
                  )}
                  helperText={
                    touched.name && errors.name
                  }
                  sx={{
                    gridColumn: {
                      xs: "span 4",
                      md: "span 2",
                    },
                  }}
                />

                <FormControl
                  fullWidth
                  error={Boolean(
                    touched.industry &&
                      errors.industry,
                  )}
                  sx={{
                    gridColumn: {
                      xs: "span 4",
                      md: "span 2",
                    },
                  }}
                >
                  <InputLabel id="industry-label">
                    Industry
                  </InputLabel>

                  <Select
                    labelId="industry-label"
                    name="industry"
                    value={values.industry}
                    label="Industry"
                    onChange={handleIndustryChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                  >
                    {formOptions.industryOptions.map(
                      (option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </MenuItem>
                      ),
                    )}
                  </Select>

                  {touched.industry &&
                    errors.industry && (
                      <FormHelperText>
                        {errors.industry}
                      </FormHelperText>
                    )}
                </FormControl>

                <FormControl
                  component="fieldset"
                  error={Boolean(
                    touched.areas && errors.areas,
                  )}
                  disabled={
                    !values.industry ||
                    loadingAreas ||
                    isSubmitting
                  }
                  sx={{
                    gridColumn: "span 4",
                    border: 1,
                    borderColor:
                      touched.areas && errors.areas
                        ? "error.main"
                        : "divider",
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{
                      xs: "flex-start",
                      sm: "center",
                    }}
                    flexDirection={{
                      xs: "column",
                      sm: "row",
                    }}
                    gap={2}
                    mb={2}
                  >
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                      >
                        Areas to include
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {!values.industry &&
                          "Select an industry before choosing operational areas"}

                        {values.industry &&
                          loadingAreas &&
                          "Loading available operational areas..."}

                        {values.industry &&
                          !loadingAreas &&
                          totalAvailableQuestions > 0 &&
                          `${selectedAreas.length} ${
                            selectedAreas.length === 1
                              ? "area"
                              : "areas"
                          } selected · ${selectedQuestionCount} ${
                            selectedQuestionCount === 1
                              ? "question"
                              : "questions"
                          }`}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={1}>
                      <ActionButton
                        type="button"
                        variant="outlined"
                        tone="ghost"
                        disabled={
                          !values.industry ||
                          loadingAreas ||
                          availableAreaSummaries.length ===
                            0 ||
                          allAvailableAreasSelected
                        }
                        onClick={handleSelectAllAreas}
                      >
                        Select All
                      </ActionButton>

                      <ActionButton
                        type="button"
                        variant="outlined"
                        tone="ghost"
                        disabled={
                          selectedAreas.length === 0 ||
                          loadingAreas
                        }
                        onClick={handleClearAllAreas}
                      >
                        Clear All
                      </ActionButton>
                    </Box>
                  </Box>

                  {loadingAreas && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      py={3}
                    >
                      <CircularProgress size={22} />

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Checking the audit question bank...
                      </Typography>
                    </Box>
                  )}

                  {!loadingAreas &&
                    values.industry &&
                    totalAvailableQuestions === 0 && (
                      <Box py={2}>
                        <Typography
                          color="warning.main"
                          fontWeight={600}
                        >
                          There are no active audit
                          questions configured for this
                          industry.
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={0.5}
                        >
                          Add or activate audit questions
                          before creating an audit for this
                          industry.
                        </Typography>
                      </Box>
                    )}

                  {!loadingAreas &&
                    values.industry &&
                    areaSummaries.length > 0 && (
                      <FormGroup
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            md: "repeat(3, minmax(0, 1fr))",
                          },
                          gap: 1,
                        }}
                      >
                        {areaSummaries.map((summary) => {
                          const hasQuestions =
                            summary.questionCount > 0;

                          return (
                            <FormControlLabel
                              key={summary.area}
                              disabled={!hasQuestions}
                              label={`${formatEnumLabel(
                                summary.area,
                              )} (${
                                summary.questionCount
                              })`}
                              control={
                                <Checkbox
                                  name="areas"
                                  value={summary.area}
                                  disabled={!hasQuestions}
                                  checked={selectedAreas.includes(
                                    summary.area,
                                  )}
                                  onChange={(event) =>
                                    handleAreaChange(
                                      summary.area,
                                      event.target.checked,
                                    )
                                  }
                                />
                              }
                              sx={{
                                opacity: hasQuestions
                                  ? 1
                                  : 0.45,
                              }}
                            />
                          );
                        })}
                      </FormGroup>
                    )}

                  {touched.areas &&
                    errors.areas && (
                      <FormHelperText>
                        {errors.areas}
                      </FormHelperText>
                    )}
                </FormControl>
              </Box>

              <Box
                display="flex"
                justifyContent="flex-end"
                mt="20px"
                gap={2}
              >
                <ActionButton
                  type="button"
                  variant="outlined"
                  tone="ghost"
                  disabled={isSubmitting}
                  onClick={() =>
                    navigate(
                      "/manageOperationalAudits",
                    )
                  }
                >
                  Cancel
                </ActionButton>

                <ActionButton
                  type="submit"
                  tone="success"
                  disabled={
                    isSubmitting ||
                    loadingAreas ||
                    !tenantId ||
                    !values.industry ||
                    selectedAreas.length === 0
                  }
                >
                  {isSubmitting
                    ? "Creating..."
                    : "Create Audit"}
                </ActionButton>
              </Box>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CreateOperationalAudits;